import {
  applyCachedTawkVisitor,
  ensureTawkVisitorBeforeFirstLoad,
  syncTawkVisitor,
} from '@/utils/tawk-visitor';

let pendingOpen = false;
let onLoadHooked = false;
let opening = false;
let openFailed = false;
let readyPollTimer = null;
let readyPollCount = 0;
/** @type {Set<(opening: boolean, failed: boolean) => void>} */
const openingListeners = new Set();

function isTawkWidgetReady() {
  return typeof window !== 'undefined' && !!window.__dsTawkWidgetReady;
}

function isTawkEmbedStarted() {
  if (typeof document === 'undefined') return false;
  try {
    return Boolean(document.querySelector('script[src*="embed.tawk.to"]'));
  } catch {
    return false;
  }
}

function forceHideTawkBubbles() {
  if (typeof window.dsTawkForceHideBubbles !== 'function') return;
  try {
    window.dsTawkForceHideBubbles();
  } catch {
    /* optional */
  }
}

function syncCloseChrome() {
  if (typeof window.dsTawkSyncCloseChrome !== 'function') return;
  try {
    window.dsTawkSyncCloseChrome();
  } catch {
    /* optional */
  }
}

function notifyOpening() {
  openingListeners.forEach((fn) => {
    try {
      fn(opening, openFailed);
    } catch {
      /* ignore listener errors */
    }
  });
  if (typeof window !== 'undefined') {
    window.__dsTawkOpening = opening;
    window.__dsTawkOpenFailed = openFailed;
    try {
      window.dispatchEvent(
        new CustomEvent('ds-tawk-opening', {
          detail: { opening, failed: openFailed },
        })
      );
    } catch {
      /* optional */
    }
  }
}

/**
 * @param {boolean} on
 */
export function setTawkOpening(on) {
  const next = Boolean(on);
  if (opening === next) return;
  opening = next;
  if (!opening) stopReadyPoll();
  notifyOpening();
}

function setTawkOpenFailed(on) {
  const next = Boolean(on);
  if (openFailed === next) return;
  openFailed = next;
  notifyOpening();
}

/** @returns {boolean} */
export function isTawkOpening() {
  return opening;
}

/** @returns {boolean} */
export function isTawkOpenFailed() {
  return openFailed;
}

/**
 * 全局客服面板是否已真正打开（勿用 KeepVisible/open 旗标：一点击就会置位，会误判「已连接」）。
 * @returns {boolean}
 */
export function isTawkSessionActive() {
  if (typeof window === 'undefined') return false;
  try {
    if (typeof document !== 'undefined') {
      if (document.documentElement.getAttribute('data-ds-tawk-panel') === '1') {
        return true;
      }
    }
    const api = window.Tawk_API;
    if (api && typeof api.isChatMaximized === 'function' && api.isChatMaximized()) {
      return true;
    }
  } catch {
    /* ignore */
  }
  return isTawkPanelReady();
}

/**
 * @param {(opening: boolean, failed?: boolean) => void} fn
 * @returns {() => void}
 */
export function subscribeTawkOpening(fn) {
  if (typeof fn !== 'function') return () => {};
  openingListeners.add(fn);
  try {
    fn(opening, openFailed);
  } catch {
    /* ignore */
  }
  return () => {
    openingListeners.delete(fn);
  };
}

function isTawkPanelReady() {
  if (typeof document === 'undefined') return false;
  try {
    if (document.documentElement.getAttribute('data-ds-tawk-panel') === '1') {
      return true;
    }
  } catch {
    /* ignore */
  }
  try {
    const api = window.Tawk_API;
    if (api && typeof api.isChatMaximized === 'function' && api.isChatMaximized()) {
      const frames = document.querySelectorAll(
        'iframe[src*="tawk.to"], iframe[title*="chat widget"], iframe[title*="Chat"], iframe[title*="chat"]'
      );
      for (let i = 0; i < frames.length; i += 1) {
        const r = frames[i].getBoundingClientRect();
        if (r.width >= 220 && r.height >= 220) return true;
      }
    }
  } catch {
    /* ignore */
  }
  return false;
}

function stopReadyPoll() {
  if (readyPollTimer != null) {
    clearInterval(readyPollTimer);
    readyPollTimer = null;
  }
  readyPollCount = 0;
}

function markOpenFailed() {
  pendingOpen = false;
  if (typeof window !== 'undefined') {
    window.__dsTawkKeepVisible = false;
    if (typeof window.dsTawkSetOpenFlag === 'function') {
      try {
        window.dsTawkSetOpenFlag(false);
      } catch {
        /* optional */
      }
    }
  }
  stopReadyPoll();
  opening = false;
  openFailed = true;
  notifyOpening();
}

function markOpenReady() {
  pendingOpen = false;
  setTawkOpenFailed(false);
  setTawkOpening(false);
}

function startReadyPoll() {
  stopReadyPoll();
  readyPollTimer = setInterval(() => {
    readyPollCount += 1;
    if (!isTawkWidgetReady()) {
      if (readyPollCount >= 60) markOpenFailed();
      return;
    }
    if (pendingOpen) {
      pendingOpen = false;
      bringTawkToFront();
    } else {
      forceHideTawkBubbles();
      syncCloseChrome();
    }
    if (isTawkPanelReady()) {
      markOpenReady();
    } else if (readyPollCount >= 60) {
      markOpenFailed();
    }
  }, 250);
}

function tryMaximizeTawk(api) {
  if (!isTawkWidgetReady() || !api) return false;
  try {
    if (typeof api.maximize === 'function') {
      api.maximize();
      forceHideTawkBubbles();
      return true;
    }
    if (typeof api.showWidget === 'function') {
      api.showWidget();
      forceHideTawkBubbles();
    }
    if (typeof api.toggle === 'function') {
      api.toggle();
      forceHideTawkBubbles();
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

function bringTawkToFront() {
  const api = window.Tawk_API || {};
  tryMaximizeTawk(api);
  forceHideTawkBubbles();
  if (typeof window.dsApplyTawkH5BubbleScale === 'function') {
    try {
      window.dsApplyTawkH5BubbleScale();
    } catch {
      /* optional */
    }
  }
  syncCloseChrome();
}

function hookTawkOnLoadOnce() {
  if (onLoadHooked) return;
  onLoadHooked = true;
  const api = window.Tawk_API || (window.Tawk_API = {});
  const prevOnLoad = typeof api.onLoad === 'function' ? api.onLoad : null;
  api.onLoad = function dsOpenTawkOnLoad() {
    window.__dsTawkWidgetReady = true;
    try {
      if (typeof prevOnLoad === 'function') prevOnLoad.apply(this, arguments);
    } catch {
      /* ignore */
    }
    if (!pendingOpen && !opening) return;
    pendingOpen = false;
    window.__dsTawkKeepVisible = true;
    if (typeof window.dsTawkSetOpenFlag === 'function') {
      try {
        window.dsTawkSetOpenFlag(true);
      } catch {
        /* optional */
      }
    }
    window.setTimeout(() => {
      bringTawkToFront();
      if (isTawkPanelReady()) markOpenReady();
    }, 120);
  };
}

if (typeof window !== 'undefined') {
  window.dsTawkSetOpening = setTawkOpening;
  window.dsTawkNotifyPanelReady = function dsTawkNotifyPanelReady() {
    if (opening) markOpenReady();
  };
  window.dsTawkNotifyOpenFailed = function dsTawkNotifyOpenFailed() {
    markOpenFailed();
  };
}

let silentPreloadScheduled = false;

/**
 * 静默注入 Tawk 脚本（不打开面板、不亮 KeepVisible）。
 * @returns {boolean}
 */
export function preloadTawkChat() {
  if (typeof window === 'undefined') return false;
  if (window.__dsTawkQuietShell) return false;
  if (typeof window.dsTawkEnsureLoaded !== 'function') return false;
  void ensureTawkVisitorBeforeFirstLoad().then(() => {
    if (window.__dsTawkQuietShell) return;
    try {
      window.dsTawkEnsureLoaded();
    } catch {
      /* ignore */
    }
  });
  return true;
}

/**
 * 站点 load 完成后空闲时静默预载一次，缩短点客服等待。
 */
export function scheduleTawkSilentPreload() {
  if (typeof window === 'undefined' || silentPreloadScheduled) return;
  silentPreloadScheduled = true;

  const run = () => {
    try {
      preloadTawkChat();
    } catch {
      /* ignore */
    }
  };

  const scheduleIdle = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 3500 });
    } else {
      window.setTimeout(run, 1200);
    }
  };

  if (typeof document !== 'undefined' && document.readyState === 'complete') {
    scheduleIdle();
    return;
  }
  window.addEventListener('load', scheduleIdle, { once: true });
}

/**
 * 打开 Tawk 客服聊天面板（不依赖默认浮球）。
 * 加载中再次点击只维持 loading，不重复 maximize；失败后再点才重试。
 * @returns {boolean}
 */
export function openTawkChat() {
  if (typeof window === 'undefined') return false;
  if (window.__dsTawkQuietShell) return false;

  if (opening) return true;

  const alreadyActive = isTawkWidgetReady() && isTawkSessionActive();
  if (alreadyActive) {
    setTawkOpenFailed(false);
    setTawkOpening(false);
    window.__dsTawkKeepVisible = true;
    if (typeof window.dsTawkSetOpenFlag === 'function') {
      try {
        window.dsTawkSetOpenFlag(true);
      } catch {
        /* optional */
      }
    }
    bringTawkToFront();
    return true;
  }

  setTawkOpenFailed(false);
  setTawkOpening(true);
  window.__dsTawkKeepVisible = true;
  if (typeof window.dsTawkSetOpenFlag === 'function') {
    try {
      window.dsTawkSetOpenFlag(true);
    } catch {
      /* optional */
    }
  }

  const startWidget = () => {
    if (window.__dsTawkQuietShell) {
      setTawkOpening(false);
      return;
    }
    applyCachedTawkVisitor();
    if (typeof window.dsTawkEnsureLoaded === 'function') {
      try {
        window.dsTawkEnsureLoaded();
      } catch {
        /* ignore */
      }
    }

    hookTawkOnLoadOnce();
    pendingOpen = true;
    startReadyPoll();

    if (isTawkWidgetReady()) {
      pendingOpen = false;
      bringTawkToFront();
      if (isTawkPanelReady()) markOpenReady();
    }
  };

  applyCachedTawkVisitor();
  void syncTawkVisitor();

  if (isTawkWidgetReady() || isTawkEmbedStarted()) {
    startWidget();
  } else {
    void ensureTawkVisitorBeforeFirstLoad().then(startWidget);
  }
  return true;
}

/**
 * 关闭 Tawk 聊天面板并重新隐藏 Widget。
 * @returns {boolean}
 */
export function closeTawkChat() {
  if (typeof window === 'undefined') return false;
  pendingOpen = false;
  setTawkOpenFailed(false);
  setTawkOpening(false);
  if (typeof window.dsTawkCloseChat === 'function') {
    try {
      window.dsTawkCloseChat();
      return true;
    } catch {
      /* fall through */
    }
  }
  window.__dsTawkKeepVisible = false;
  if (typeof window.dsTawkSetOpenFlag === 'function') {
    try {
      window.dsTawkSetOpenFlag(false);
    } catch {
      /* optional */
    }
  }
  try {
    const api = window.Tawk_API;
    if (api && isTawkWidgetReady()) {
      if (typeof api.minimize === 'function') api.minimize();
      if (typeof api.hideWidget === 'function') api.hideWidget();
    }
  } catch {
    return false;
  }
  forceHideTawkBubbles();
  return true;
}
